import { useEffect, useRef, useState } from "react";
import { api, imgUrl, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, X, Save, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const GALLERIES = [
  { key: "home_gallery", label: "Homepage — Recent Work", hint: "The big photo grid on the homepage." },
  { key: "models_gallery", label: "Models Page — Rotating Gallery", hint: "Auto-scrolling strip at the top of the Models page." },
  { key: "cars_gallery", label: "Cars Page — Rotating Gallery", hint: "Auto-scrolling strip at the top of the Cars page." },
  { key: "mv_photos", label: "Homepage — Shooting a Music Video", hint: "3 photos in the music video section." },
];

const TEXT_FIELDS = [
  { key: "hero_title", label: "Hero headline" },
  { key: "hero_tagline", label: "Hero tagline" },
  { key: "models_sub", label: "Models page intro text" },
  { key: "cars_sub", label: "Cars page intro text" },
  { key: "houses_sub", label: "Houses page intro text" },
  { key: "marketing_sub", label: "Marketing page intro text" },
];

function GalleryEditor({ value, onSave, testId }) {
  const [images, setImages] = useState(value || []);
  const [urlInput, setUrlInput] = useState("");
  const fileRef = useRef(null);

  useEffect(() => { setImages(value || []); }, [value]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd);
      setImages((prev) => [...prev, data.url]);
      toast.success("Image uploaded — click Save to publish.");
    } catch (err) { toast.error(formatApiError(err)); }
    e.target.value = "";
  };

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        {images.map((img, i) => (
          <div key={i} data-testid={`${testId}-item-${i}`} className="relative">
            <img src={imgUrl(img)} alt="" className="w-full h-36 object-cover border border-white/10" />
            <button onClick={() => setImages(images.filter((_, j) => j !== i))} data-testid={`${testId}-remove-${i}`}
              className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 rounded-full p-1.5 transition-colors" aria-label="Remove">
              <X className="w-3.5 h-3.5 text-white" />
            </button>
          </div>
        ))}
        {images.length === 0 && <p className="text-sm text-zinc-500 col-span-full">No images — page falls back to placeholders.</p>}
      </div>
      <div className="flex flex-wrap gap-3 items-center">
        <Input placeholder="Paste image URL" value={urlInput} onChange={(e) => setUrlInput(e.target.value)}
          className="bg-white/5 border-white/10 max-w-md" data-testid={`${testId}-url-input`} />
        <button onClick={() => { if (urlInput.trim()) { setImages([...images, urlInput.trim()]); setUrlInput(""); } }}
          className="text-xs border border-white/15 hover:border-brand px-4 py-2.5 transition-colors text-zinc-300" data-testid={`${testId}-add-url`}>
          Add URL
        </button>
        <Label className="flex items-center gap-2 text-xs border border-white/15 hover:border-brand px-4 py-2.5 transition-colors text-zinc-300 cursor-pointer">
          <Upload className="w-3.5 h-3.5" /> Upload
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} />
        </Label>
        <button onClick={() => onSave(images)} data-testid={`${testId}-save`}
          className="flex items-center gap-2 bg-brand hover:bg-brand-hover text-white text-xs font-semibold tracking-widest uppercase px-5 py-2.5 transition-colors">
          <Save className="w-4 h-4" /> Save
        </button>
      </div>
    </div>
  );
}

export default function SiteAdmin() {
  const [content, setContent] = useState(null);
  const [texts, setTexts] = useState({});
  const [testimonials, setTestimonials] = useState([]);

  const refresh = () => api.get("/admin/site-content").then((r) => {
    setContent(r.data || {});
    setTexts(r.data?.site_text || {});
    setTestimonials(r.data?.testimonials || []);
  }).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { refresh(); }, []);

  const saveKey = async (key, value, msg = "Saved — live on the site.") => {
    try {
      await api.put("/admin/site-content", { key, value });
      toast.success(msg);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-site">
      <h1 className="font-display text-4xl text-white mb-2">Site Content</h1>
      <p className="text-sm text-zinc-500 mb-8">Update photos, text and testimonials across the site — no code changes needed.</p>
      {content === null ? <p className="text-zinc-500">Loading…</p> : (
        <Tabs defaultValue="home_gallery">
          <TabsList className="bg-[#0A0A0A] border border-white/10 mb-8 flex-wrap h-auto">
            {GALLERIES.map((g) => <TabsTrigger key={g.key} value={g.key} data-testid={`site-tab-${g.key}`}>{g.label.split("—")[0]}</TabsTrigger>)}
            <TabsTrigger value="testimonials" data-testid="site-tab-testimonials">Testimonials</TabsTrigger>
            <TabsTrigger value="texts" data-testid="site-tab-texts">Site Text</TabsTrigger>
          </TabsList>

          {GALLERIES.map((g) => (
            <TabsContent key={g.key} value={g.key}>
              <h2 className="text-sm font-semibold text-white mb-1">{g.label}</h2>
              <p className="text-xs text-zinc-500 mb-5">{g.hint}</p>
              <GalleryEditor value={content[g.key]} testId={`gallery-${g.key}`}
                onSave={(images) => saveKey(g.key, images)} />
            </TabsContent>
          ))}

          <TabsContent value="testimonials">
            <h2 className="text-sm font-semibold text-white mb-5">Testimonials (shown on homepage)</h2>
            <div className="space-y-3 mb-6">
              {testimonials.map((t, i) => (
                <div key={i} className="border border-white/10 bg-[#0A0A0A] p-4 flex justify-between gap-4" data-testid={`testimonial-admin-${i}`}>
                  <div>
                    <p className="text-sm text-zinc-300">"{t.text}"</p>
                    <p className="text-xs text-zinc-500 mt-1">— {t.name}</p>
                  </div>
                  <button onClick={() => setTestimonials(testimonials.filter((_, j) => j !== i))}
                    className="text-red-400 hover:text-red-300 self-start" aria-label="Remove">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <TestimonialForm onAdd={(t) => setTestimonials([...testimonials, t])} />
            <button onClick={() => saveKey("testimonials", testimonials, "Testimonials saved.")} data-testid="testimonials-save"
              className="mt-6 flex items-center gap-2 bg-brand hover:bg-brand-hover text-white text-xs font-semibold tracking-widest uppercase px-5 py-3 transition-colors">
              <Save className="w-4 h-4" /> Save Testimonials
            </button>
          </TabsContent>

          <TabsContent value="texts">
            <h2 className="text-sm font-semibold text-white mb-5">Editable site text</h2>
            <div className="space-y-4 max-w-2xl">
              {TEXT_FIELDS.map((f) => (
                <div key={f.key}>
                  <Label className="text-xs text-zinc-400">{f.label}</Label>
                  <Textarea rows={2} value={texts[f.key] || ""} data-testid={`text-${f.key}`}
                    onChange={(e) => setTexts({ ...texts, [f.key]: e.target.value })}
                    className="bg-white/5 border-white/10 mt-1" />
                </div>
              ))}
              <button onClick={() => saveKey("site_text", texts, "Site text saved.")} data-testid="texts-save"
                className="flex items-center gap-2 bg-brand hover:bg-brand-hover text-white text-xs font-semibold tracking-widest uppercase px-5 py-3 transition-colors">
                <Save className="w-4 h-4" /> Save Text
              </button>
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function TestimonialForm({ onAdd }) {
  const [t, setT] = useState({ name: "", text: "" });
  return (
    <div className="border border-white/10 bg-[#0A0A0A] p-5 flex flex-wrap gap-3 items-end">
      <div className="flex-1 min-w-40">
        <Label className="text-xs text-zinc-400">Name</Label>
        <Input value={t.name} onChange={(e) => setT({ ...t, name: e.target.value })} data-testid="testimonial-name"
          className="bg-white/5 border-white/10 mt-1" placeholder="Artist name" />
      </div>
      <div className="flex-[2] min-w-60">
        <Label className="text-xs text-zinc-400">Quote</Label>
        <Input value={t.text} onChange={(e) => setT({ ...t, text: e.target.value })} data-testid="testimonial-text"
          className="bg-white/5 border-white/10 mt-1" placeholder="What they said…" />
      </div>
      <button onClick={() => { if (t.name && t.text) { onAdd(t); setT({ name: "", text: "" }); } }} data-testid="testimonial-add"
        className="flex items-center gap-2 border border-white/15 hover:border-brand text-zinc-300 text-xs px-4 py-2.5 transition-colors">
        <Plus className="w-3.5 h-3.5" /> Add
      </button>
    </div>
  );
}
